import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import type { PriceImprovementStatus } from 'state/quotes/priceEnhancements/priceImprovementRequest/priceImprovementRequest.model';
import { roundNumberToPrecision } from 'utils/core/math';

import { PriceImprovementRequestForm } from './form/RequestForm';
import { PriceImprovementResponseForm } from './form/ResponseForm';
import type { PriceImprovementProps } from './PriceImprovement.connect';
import { StatusBadge } from './StatusBadge';

export function PriceImprovementRaw({
  isExpanded,
  status,
  traderComment,
  validatedPriceAdjustment,
  requestedTradingOffer,
  validatedTradingOffer,
  onExpanded,
  onDiscard,
  onFieldValueChanged,
  expirationTime,
  ...requestFormProps
}: PriceImprovementProps) {
  const onApply = useCallback(() => {
    if (validatedTradingOffer !== undefined) {
      onFieldValueChanged('tradingOffer', roundNumberToPrecision(validatedTradingOffer / 100, 6));
    }
  }, [onFieldValueChanged, validatedTradingOffer]);

  const onToggleAccordion = useCallback(() => {
    onExpanded(!isExpanded);
  }, [isExpanded, onExpanded]);

  return isExpanded ? (
    <div className="w-100 border border-top-0">
      <div className="card border-top">
        <div className="p-0 card-header">
          <button
            className="btn btn-flat-primary text-large w-100 d-flex flex-row align-items-center"
            onClick={onToggleAccordion}
          >
            <span className="flex-grow-1 text-start">
              <FormattedMessage id="priceImprovement.header.expanded" />
            </span>
            {status !== 'Initialized' && status !== undefined && <StatusBadge status={status} />}
            <em className="icon icon-sm me-1 text-secondary">keyboard_arrow_up</em>
          </button>
        </div>

        <div className="collapse show">
          <div className="card-body p-3">
            {status !== undefined && isResponseFormDisplayed(status) && (
              <PriceImprovementResponseForm
                status={status}
                onApply={onApply}
                onDiscard={onDiscard}
                comment={traderComment}
                validatedPriceAdjustment={validatedPriceAdjustment}
                requestedTradingOffer={requestedTradingOffer}
                validatedTradingOffer={validatedTradingOffer}
              />
            )}
            {status !== undefined && expirationTime !== undefined && !isResponseFormDisplayed(status) && (
              <PriceImprovementRequestForm
                status={status}
                onDiscard={onDiscard}
                requestedTradingOffer={requestedTradingOffer}
                expirationTime={expirationTime}
                {...requestFormProps}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  ) : (
    <button className="btn btn-outline-primary w-100" onClick={onToggleAccordion}>
      <FormattedMessage id="priceImprovement.header.collapsed" />
    </button>
  );
}

function isResponseFormDisplayed(status: PriceImprovementStatus) {
  return status === 'Validated' || status === 'Rejected';
}
